import { TFunction } from 'i18next';
import { API } from 'api';
import { Yup } from 'shared/yup-extended';
import { IapCheckoutDetails } from 'features/payments/hooks/useIapState';
import {
  EmailStatus,
  getEmailValidationSchema,
} from 'shared/getEmailValidationSchema';
import * as Sentry from '@sentry/capacitor';

export type PreProcessIapPurchaseData = {
  externalAccountCode: string;
  email: string;
  firstName: string;
  lastName: string;
  optInToUpdates: boolean;
};

export type FormValues = Pick<
  IapCheckoutDetails,
  'email' | 'firstName' | 'lastName' | 'optInToUpdates'
>;

/**
 * Extracts numeric price from a formatted price string.
 * Removes currency symbols and other non-numeric characters, keeping only digits and decimal point.
 * Example: "$9.99" -> 9.99
 */
const extractPriceFromString = (priceString: string): number => {
  return parseFloat(priceString.replace(/[^0-9.]/g, '')) || 0;
};

/**
 * Gets the formatted price string for a product directly from the CdvPurchase store.
 * Returns empty string if the product or price cannot be found.
 * Example: "$24.99"
 */
export const getFormattedPriceFromStore = (productId: string): string => {
  const product = CdvPurchase.store.get(
    productId,
    CdvPurchase.Platform.APPLE_APPSTORE,
  );

  const price = product?.offers?.[0]?.pricingPhases?.[0]?.price;

  if (!price) {
    Sentry.captureMessage('iap-store-no-price-found', {
      level: 'warning',
      extra: {
        productId,
      },
    });
  }

  return price || '';
};

/**
 * Gets the numeric price for a product directly from the CdvPurchase store.
 * Returns 0 if the product or price cannot be found.
 * Example: 24.99
 */
export const getPriceFromStore = (productId: string): number => {
  const priceString = getFormattedPriceFromStore(productId);
  return extractPriceFromString(priceString);
};

export const getValidationSchema = (
  t: TFunction,
  isMembershipUpgrade: boolean,
  cache: Map<string, EmailStatus>,
): Yup.SchemaOf<FormValues> =>
  Yup.object({
    firstName: Yup.string().required(
      t('membershipCheckout.inputs.firstName.required'),
    ),
    lastName: Yup.string().required(
      t('membershipCheckout.inputs.lastName.required'),
    ),
    email: getEmailValidationSchema({
      cache,
      skip: isMembershipUpgrade,
      requiredErrMsg: t('membershipCheckout.inputs.email.required'),
      invalidErrMsg: t('membershipCheckout.inputs.email.invalid'),
      registeredErrMsg: t('membershipCheckout.registeredEmail'),
      managedErrMsg: t('organization.managedAccount'),
    }),
    optInToUpdates: Yup.boolean().required(
      t('membershipCheckout.inputs.optInToUpdates.required'),
    ),
  });

export const preProcessPurchaseIapApi = async (
  data: PreProcessIapPurchaseData,
): Promise<void> => {
  await API.post('recurly_integration/pre_create_iap/', data);
};

export const handleValidatorSuccess = (
  callback: CdvPurchase.Callback<CdvPurchase.Validator.Response.Payload>,
  transaction: CdvPurchase.Validator.Response.NativeTransaction,
  productId: string,
): void => {
  callback({
    ok: true,
    data: {
      id: productId,
      latest_receipt: true,
      transaction,
    },
  });
};
